import { computed, reactive, ref } from "vue";

import { useBaseComposable } from "./useBaseComposable";

import { AuthService } from "@/services";

import { isLoading } from "@/shared/base.service";

import { useAuthStore } from "@/stores/auth.store";

import { startTokenExpirationWatcher } from "@/utils/tokenWatcher";
import { useUser } from "@/composables/useUser";

export function useAuth() {
  const { createValidationRules, required, minLength, email, sameAs } =
    useBaseComposable();

  const passwordSuggestions = ref<string[]>([]); 

  const signUpFormRules = createValidationRules({
    login: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      email: {
        validator: email,
        messageKey: "validation.email",
      },
    },
    password: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minLength: {
        validator: minLength(6),
        messageKey: "validation.minLength",
        params: { min: 6 },
      },
    },
    confirmationPassword: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minLength: {
        validator: minLength(6),
        messageKey: "validation.minLength",
        params: { min: 6 },
      },
      sameAsPassword: {
        validator: sameAs(computed(() => signUpForm.password)),
        messageKey: "validation.sameAsPassword",
      },
    },
  });

  const signUpForm = reactive({
    login: "",
    password: "",
    confirmationPassword: "",
    permissions: [],
  });

  const signInFormRules = createValidationRules({
    login: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      email: {
        validator: email,
        messageKey: "validation.email",
      },
    },
    password: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minLength: {
        validator: minLength(3),
        messageKey: "validation.minLength",
        params: { min: 3 },
      },
    },
  });

  const signInForm = reactive({
    login: "",
    password: "",
    remember: false,
  });

  const forgotPasswordFormRules = createValidationRules({
    login: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      email: {
        validator: email,
        messageKey: "validation.email",
      },
    },
  });

  const forgotPasswordForm = reactive({
    login: "",
  });

  const passwordResetFormRules = createValidationRules({
    password: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minLength: {
        validator: minLength(6),
        messageKey: "validation.minLength",
        params: { min: 6 },
      },
    },
    confirmationPassword: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minLength: {
        validator: minLength(6),
        messageKey: "validation.minLength",
        params: { min: 6 },
      },
      sameAsPassword: {
        validator: sameAs(computed(() => passwordResetForm.password)),
        messageKey: "validation.sameAsPassword",
      },
    },
  });

  const passwordResetForm = reactive({
    password: "",
    confirmationPassword: "",
  });

  const autoSignIn = async () => {
    const storedAuth = useAuthStore().getAutoSign;

    if (storedAuth) {
      const { login, token } = storedAuth;
      signInForm.login = login;
      useAuthStore().setToken(token);

      return true;
    }

    return false;
  };

  async function signIn(login: string, password: string) {
    try {
      const response = await AuthService.SignIn(login, password);
      if (response?.data.mfa_required === false) {
        const expiresAt = Date.now() + 15 * 60 * 1000; // 15 minutos (DRF-style)

        useAuthStore().setToken(response.data.access_token);
        useAuthStore().setExpiresAt(expiresAt);
        
        // Salvar refresh_token (DRF-style)
        if (response.data.refresh_token) {
          useAuthStore().setRefreshToken(response.data.refresh_token);
        }
        
        // Resetar flag de refresh no novo login
        useAuthStore().setHasRefreshed(false);

        // Captura apenas billing_status no login e salva no auth store
      try {
        const billing = (response as any)?.data?.billing_status;
        console.log("[Auth] signIn billing_status:", billing);
        if (typeof billing !== "undefined") {
          useAuthStore().setBillingStatus(!!billing);
        }
      } catch (_) { /* ignore */ void 0; }

        startTokenExpirationWatcher();

        // Busca e salva dados do usuário (inclui campo 'paid')
        try { await useUser().getUserData(); } catch (e) { console.error(e); }

        if (signInForm.remember == true) {
          useAuthStore().setAutoSign({
            login: signInForm.login,
            token: response.data.access_token,
          });
        }
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function verifyMfa(mfa_code: string, mfa_session_token: string, trust_device: boolean) {
    try {
      const response = await AuthService.VerifyMfa(mfa_code, mfa_session_token, trust_device);
      if (response) {
        const expiresAt = Date.now() + 15 * 60 * 1000; // 15 minutos (DRF-style)

        useAuthStore().setToken(response.data.access_token);
        useAuthStore().setExpiresAt(expiresAt);
        
        // Salvar refresh_token (DRF-style)
        if (response.data.refresh_token) {
          useAuthStore().setRefreshToken(response.data.refresh_token);
        }
        
        // Resetar flag de refresh no MFA
        useAuthStore().setHasRefreshed(false);

        // Captura apenas billing_status no MFA e salva no auth store
      try {
        const billing = (response as any)?.data?.billing_status;
        console.log("[Auth] verifyMfa billing_status:", billing);
        if (typeof billing !== "undefined") {
          useAuthStore().setBillingStatus(!!billing);
        }
      } catch (_) { console.error("Error mapping billing status"); }

        console.log("[Auth] MFA verificado");
        startTokenExpirationWatcher();

        // Busca e salva dados do usuário (inclui campo 'paid')
        try { await useUser().getUserData(); } catch (e) { console.error(e); }

        if (signInForm.remember == true) {
          useAuthStore().setAutoSign({
            login: signInForm.login,
            token: response.data.access_token,
          });
        }
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function signInWithGoogle(id_token: string) {
    try {
      const response = await AuthService.HandleGoogleAuth(id_token);
      if (response) {
        const expiresAt = Date.now() + 15 * 60 * 1000; // 15 minutos (DRF-style)

        useAuthStore().setToken(response.data.access_token);
        useAuthStore().setExpiresAt(expiresAt);
        
        // Salvar refresh_token (DRF-style)
        if (response.data.refresh_token) {
          useAuthStore().setRefreshToken(response.data.refresh_token);
        }
        
        // Resetar flag de refresh no Google login
        useAuthStore().setHasRefreshed(false);

        // Captura apenas billing_status no Google e salva no auth store
      try {
        const billing = (response as any)?.data?.billing_status;
        console.log("[Auth] google billing_status:", billing);
        if (typeof billing !== "undefined") {
          useAuthStore().setBillingStatus(!!billing);
        }
      } catch (_) { /* ignore */ void 0; }

        console.log("[Auth] Login Google bem-sucedido");
        startTokenExpirationWatcher();

        // Busca e salva dados do usuário (inclui campo 'paid')
        try { await useUser().getUserData(); } catch (e) { console.error(e); }

        if (signInForm.remember == true) {
          useAuthStore().setAutoSign({
            login: "", //signInForm.login,
            token: response.data.access_token,
          });
        }
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function signUp(payload: object) {
    try {
      const response = await AuthService.SignUp(payload);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function forgotPassword(payload: object) {
    try {
      const response = await AuthService.ForgotPassword(payload);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function passwordReset(payload: object, token?: string) {
    try {
      const response = await AuthService.PasswordReset(payload, token);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function refreshToken() {
    try {
      // Recuperar refresh_token do localStorage
      const currentRefreshToken = useAuthStore().getRefreshToken;
      
      if (!currentRefreshToken) {
        console.error('[RefreshToken] Nenhum refresh_token encontrado');
        return null;
      }

      // DRF-style: envia refresh_token no body
      const response = await AuthService.RefreshToken(currentRefreshToken);

      if (response) {
        const expiresAt = Date.now() + 4 * 60 * 60 * 1000; // 4 horas
        
        // Atualiza access_token
        useAuthStore().setToken(response.data.access_token);
        useAuthStore().setExpiresAt(expiresAt);
        
        // Atualiza refresh_token (token rotation)
        if (response.data.refresh_token) {
          useAuthStore().setRefreshToken(response.data.refresh_token);
        }
        
        // Marcar que já fez refresh (permitir apenas 1 refresh por sessão)
        useAuthStore().setHasRefreshed(true);
        
        // Aguarda um tick para garantir que localStorage foi atualizado
        await new Promise(resolve => setTimeout(resolve, 10));
        
        console.log('[RefreshToken] Token renovado com sucesso - próxima expiração forçará logout');
      } else {
        console.error('[RefreshToken] Response vazio ou null');
      }

      return response;
    } catch (error) {
      console.error('[RefreshToken] Erro:', error);
      return null;
    }
  }

  async function logout() {
    try {
      const response = await AuthService.Logout();

      if (response) useAuthStore().setLogout();

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  return {
    isLoading,
    signUpFormRules,
    signUpForm,
    signInFormRules,
    signInForm,
    forgotPasswordFormRules,
    forgotPasswordForm,
    passwordResetFormRules,
    passwordResetForm,
    autoSignIn,
    signIn,
    signUp,
    forgotPassword,
    passwordReset,
    refreshToken,
    logout,
    verifyMfa,
    signInWithGoogle,
    passwordSuggestions
  };
}
